import React, { useRef, useState, useEffect, useMemo } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import html2canvas from 'html2canvas';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { buildWorkshopLotScanUrl } from '../../../services/lotScanResolve';
import { isProductionManagerRole } from '../../../lib/operatorRoles';

const QRCodeGenerator = ({ batches, selectedBatch, onBatchSelect, userRole, currentUser }) => {
  const qrRef = useRef(null);
  const [caseFilter, setCaseFilter] = useState('all');
  const [filteredBatches, setFilteredBatches] = useState([]);
  const [uniqueCases, setUniqueCases] = useState([]);
  const [loadingCases, setLoadingCases] = useState(false);

  // Filter batches based on user role
  const visibleBatches = useMemo(() => {
    if (!batches) return [];
    
    if (isProductionManagerRole(userRole) && currentUser?.id) {
      return batches.filter(batch => 
        batch.responsible === currentUser.id || 
        batch.responsibleId === currentUser.id
      );
    }
    
    return batches;
  }, [batches, userRole, currentUser]);

  // Extract unique cases from visible batches
  useEffect(() => {
    const fetchCases = async () => {
      if (!visibleBatches || visibleBatches.length === 0) {
        setUniqueCases([]);
        return;
      }

      try {
        setLoadingCases(true);
        
        // Get unique case_ids from visible batches
        const caseIds = [...new Set(visibleBatches.map(batch => batch?.case_id).filter(Boolean))];
        
        if (caseIds.length === 0) {
          setUniqueCases([]);
          setLoadingCases(false);
          return;
        }

        // Fetch case details from Supabase
        const { data: casesData, error } = await supabase
          .from('cases')
          .select('id, case_number, patient:patients(id, name)')
          .in('id', caseIds)
          .order('case_number', { ascending: false });

        if (error) {
          console.error('Supabase error:', error);
          throw error;
        }

        const casesMap = casesData?.map(caseItem => ({
          id: caseItem.id,
          caseNumber: caseItem.case_number,
          patientName: caseItem.patient?.name || 'Patient inconnu',
          // Count batches for this case from visible batches
          batchCount: visibleBatches.filter(b => b.case_id === caseItem.id).length
        })) || [];

        console.log('Fetched cases:', casesMap);
        setUniqueCases(casesMap);
      } catch (error) {
        console.error('Error fetching cases:', error);
        setUniqueCases([]);
      } finally {
        setLoadingCases(false);
      }
    };

    fetchCases();
  }, [visibleBatches]);

  // Filter visible batches based on selected case
  useEffect(() => {
    if (!visibleBatches) {
      setFilteredBatches([]);
      return;
    }

    if (caseFilter === 'all') {
      setFilteredBatches(visibleBatches);
    } else {
      setFilteredBatches(visibleBatches.filter(batch => batch?.case_id === caseFilter));
    }
  }, [visibleBatches, caseFilter]);

  const generateQRData = (batch) => {
    const id = batch?.id;
    if (id && typeof window !== 'undefined') {
      const url = buildWorkshopLotScanUrl(id);
      if (url) return url;
    }
    return `LOT-${batch?.batchNumber || batch?.batch_number}-${Date.now()}`;
  };

  const handlePrint = () => {
    if (!qrRef?.current) return;

    html2canvas(qrRef?.current).then(canvas => {
      const imgData = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = `QR-${selectedBatch?.batchNumber || selectedBatch?.batch_number}.png`;
      link.href = imgData;
      link.click();
    });
  };

  const handlePrintProductionSheet = () => {
    window.print();
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'En attente', className: 'bg-yellow-100 text-yellow-800' },
      in_progress: { label: 'En cours', className: 'bg-blue-100 text-blue-800' },
      quality_check: { label: 'Contrôle qualité', className: 'bg-purple-100 text-purple-800' },
      packaging: { label: 'Emballage', className: 'bg-orange-100 text-orange-800' },
      completed: { label: 'Terminé', className: 'bg-green-100 text-green-800' },
      cancelled: { label: 'Annulé', className: 'bg-red-100 text-red-800' },
      on_hold: { label: 'En pause', className: 'bg-gray-100 text-gray-800' }
    };
    return statusConfig[status] || statusConfig.pending;
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      low: { label: 'Basse', className: 'bg-gray-100 text-gray-700' },
      medium: { label: 'Moyenne', className: 'bg-blue-100 text-blue-700' },
      high: { label: 'Haute', className: 'bg-orange-100 text-orange-700' },
      urgent: { label: 'Urgente', className: 'bg-red-100 text-red-700' }
    };
    return priorityConfig[priority] || priorityConfig.medium;
  };

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatDateTime = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Batch Selection */}
      <div className="lg:col-span-1">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          {/* User Role Notice */}
          {isProductionManagerRole(userRole) && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2 text-sm text-blue-800">
                <Icon name="Info" size={16} />
                <span className="font-medium">Mes lots uniquement</span>
              </div>
            </div>
          )}

          {/* Case Filter */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filtrer par cas
            </label>
            {loadingCases ? (
              <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500">
                Chargement des cas...
              </div>
            ) : (
              <select
                value={caseFilter}
                onChange={(e) => {
                  console.log('Selected case:', e.target.value);
                  setCaseFilter(e.target.value);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="all">
                  Tous les cas ({visibleBatches?.length || 0} lots)
                </option>
                {uniqueCases.length > 0 ? (
                  uniqueCases.map(caseItem => (
                    <option key={caseItem.id} value={caseItem.id}>
                      {caseItem.caseNumber} - {caseItem.patientName} ({caseItem.batchCount} lot{caseItem.batchCount > 1 ? 's' : ''})
                    </option>
                  ))
                ) : (
                  <option disabled>Aucun cas disponible</option>
                )}
              </select>
            )}
          </div>

          {/* Batch List Header */}
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-gray-900">
              Lots de production
            </h3>
            <span className="text-sm text-gray-500">
              {filteredBatches?.length || 0} lot(s)
            </span>
          </div>

          {/* Batch List */}
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {filteredBatches?.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Icon name="Package" size={32} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">
                  {isProductionManagerRole(userRole)
                    ? 'Aucun lot ne vous est assigné'
                    : 'Aucun lot trouvé'}
                </p>
              </div>
            ) : (
              filteredBatches?.map(batch => {
                const statusBadge = getStatusBadge(batch?.status);
                const priorityBadge = getPriorityBadge(batch?.priority);

                return (
                  <button
                    key={batch?.id}
                    onClick={() => onBatchSelect(batch)}
                    className={`w-full text-left p-3 rounded-lg border transition-colors ${
                      selectedBatch?.id === batch?.id
                        ? 'border-primary bg-primary/5'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="font-medium text-gray-900">
                        {batch?.batchNumber || batch?.batch_number}
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${priorityBadge.className}`}>
                        {priorityBadge.label}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600">{batch?.patient_name || batch?.patientName}</div>
                    <div className="flex items-center gap-2 mt-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusBadge.className}`}>
                        {statusBadge.label}
                      </span>
                      {batch?.current_phase && (
                        <span className="px-2 py-0.5 rounded bg-purple-100 text-purple-700 text-xs font-medium">
                          Phase {batch.current_phase}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* QR Code Display */}
      <div className="lg:col-span-2">
        {selectedBatch ? (
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">
                Fiche de Production Complète
              </h3>
              <div className="flex gap-2">
                <Button variant="outline" iconName="Download" onClick={handlePrint}>
                  Télécharger QR
                </Button>
                <Button variant="primary" iconName="Printer" onClick={handlePrintProductionSheet}>
                  Imprimer Fiche
                </Button>
              </div>
            </div>

            {/* Production Sheet Preview */}
            <div ref={qrRef} className="border-2 border-dashed border-gray-300 rounded-lg p-8 bg-white">
              {/* Header */}
              <div className="text-center mb-6 pb-4 border-b-2 border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Fiche de Production</h2>
                <div className="text-sm text-gray-600">ClearMotion - Orthodontie</div>
                <div className="text-xs text-gray-500 mt-1">
                  Généré le {formatDateTime(new Date())}
                </div>
              </div>

              {/* Informations principales */}
              <div className="grid grid-cols-2 gap-6 mb-6">
                <div className="col-span-2 bg-primary/5 rounded-lg p-4">
                  <div className="text-sm font-medium text-gray-500 mb-1">Numéro de lot</div>
                  <div className="text-2xl font-bold text-primary">
                    {selectedBatch?.batchNumber || selectedBatch?.batch_number}
                  </div>
                </div>

                <div>
                  <div className="text-sm font-medium text-gray-500 mb-1">Patient</div>
                  <div className="text-lg font-semibold text-gray-900">
                    {selectedBatch?.patient_name || selectedBatch?.patientName}
                  </div>
                </div>

                <div>
                  <div className="text-sm font-medium text-gray-500 mb-1">Clinique</div>
                  <div className="text-lg font-semibold text-gray-900">
                    {selectedBatch?.clinic_name || selectedBatch?.clinicName}
                  </div>
                </div>

                <div>
                  <div className="text-sm font-medium text-gray-500 mb-1">Statut</div>
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                    getStatusBadge(selectedBatch?.status).className
                  }`}>
                    {getStatusBadge(selectedBatch?.status).label}
                  </span>
                </div>

                <div>
                  <div className="text-sm font-medium text-gray-500 mb-1">Priorité</div>
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                    getPriorityBadge(selectedBatch?.priority).className
                  }`}>
                    {getPriorityBadge(selectedBatch?.priority).label}
                  </span>
                </div>

                {selectedBatch?.current_phase && (
                  <div className="col-span-2">
                    <div className="text-sm font-medium text-gray-500 mb-1">Phase actuelle</div>
                    <div className="text-lg font-semibold text-purple-600">
                      Phase {selectedBatch.current_phase}
                    </div>
                  </div>
                )}
              </div>

              {/* Dates importantes */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">📅 Planning</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs font-medium text-gray-500 mb-1">Date de début</div>
                    <div className="text-sm text-gray-900">
                      {formatDate(selectedBatch?.start_date || selectedBatch?.startDate)}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs font-medium text-gray-500 mb-1">Échéance</div>
                    <div className="text-sm font-semibold text-red-600">
                      {formatDate(selectedBatch?.deadline)}
                    </div>
                  </div>

                  {selectedBatch?.completion_date && (
                    <div>
                      <div className="text-xs font-medium text-gray-500 mb-1">Date de fin</div>
                      <div className="text-sm text-green-600">
                        {formatDate(selectedBatch.completion_date)}
                      </div>
                    </div>
                  )}

                  {selectedBatch?.packaging_date && (
                    <div>
                      <div className="text-xs font-medium text-gray-500 mb-1">Date d'emballage</div>
                      <div className="text-sm text-gray-900">
                        {formatDate(selectedBatch.packaging_date)}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Modèles et Labels */}
              <div className="bg-blue-50 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">🦷 Modèles et Étiquettes</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs font-medium text-gray-500 mb-2">Modèle Supérieur</div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm bg-white px-2 py-1 rounded border border-blue-200">
                        De: {selectedBatch?.model_up_from || 0}
                      </span>
                      <span className="text-sm bg-white px-2 py-1 rounded border border-blue-200">
                        À: {selectedBatch?.model_up_to || 0}
                      </span>
                    </div>
                    <div className="text-xs text-blue-700 mt-1">
                      Total: {(selectedBatch?.model_up_to || 0) - (selectedBatch?.model_up_from || 0) + 1} aligneurs
                    </div>
                  </div>

                  <div>
                    <div className="text-xs font-medium text-gray-500 mb-2">Modèle Inférieur</div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm bg-white px-2 py-1 rounded border border-blue-200">
                        De: {selectedBatch?.model_low_from || 0}
                      </span>
                      <span className="text-sm bg-white px-2 py-1 rounded border border-blue-200">
                        À: {selectedBatch?.model_low_to || 0}
                      </span>
                    </div>
                    <div className="text-xs text-blue-700 mt-1">
                      Total: {(selectedBatch?.model_low_to || 0) - (selectedBatch?.model_low_from || 0) + 1} aligneurs
                    </div>
                  </div>

                  <div>
                    <div className="text-xs font-medium text-gray-500 mb-2">Étiquettes</div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm bg-white px-2 py-1 rounded border border-blue-200">
                        De: {selectedBatch?.label_from || 0}
                      </span>
                      <span className="text-sm bg-white px-2 py-1 rounded border border-blue-200">
                        À: {selectedBatch?.label_to || 0}
                      </span>
                    </div>
                  </div>

                  <div>
                    <div className="text-xs font-medium text-gray-500 mb-2">Nombre OF</div>
                    <div className="text-sm font-semibold bg-white px-2 py-1 rounded border border-blue-200">
                      {selectedBatch?.of_number || 0}
                    </div>
                  </div>
                </div>
              </div>

              {/* Détails techniques */}
              <div className="bg-green-50 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">⚙️ Détails Techniques</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <div className="text-xs font-medium text-gray-500 mb-1">Élastique</div>
                    <div className="text-sm font-semibold text-gray-900">
                      {selectedBatch?.elastic || '—'}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs font-medium text-gray-500 mb-1">Description</div>
                    <div className="text-sm font-semibold text-gray-900">
                      {selectedBatch?.ali_confirmation || '—'}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs font-medium text-gray-500 mb-1">Suppression</div>
                    <div className="text-sm font-semibold text-gray-900">
                      {selectedBatch?.suppression || '—'}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs font-medium text-gray-500 mb-1">Progression</div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-green-600 h-2 rounded-full"
                          style={{ width: `${selectedBatch?.progress || 0}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-semibold text-gray-900">
                        {selectedBatch?.progress || 0}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* QR Code */}
              <div className="flex justify-center py-6 mb-6">
                <div className="bg-white p-4 rounded-lg border-4 border-gray-800">
                  <QRCodeSVG
                    value={generateQRData(selectedBatch)}
                    size={200}
                    level="H"
                    includeMargin={true}
                  />
                  <div className="text-center mt-2 text-xs text-gray-600 font-mono break-all">
                    {generateQRData(selectedBatch)}
                  </div>
                </div>
              </div>

              {/* Notes */}
              {selectedBatch?.notes && (
                <div className="bg-yellow-50 rounded-lg p-4 mb-6">
                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <Icon name="MessageSquare" size={16} />
                    Notes importantes
                  </h4>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">
                    {selectedBatch.notes}
                  </p>
                </div>
              )}

              {/* Instructions */}
              

              {/* Footer avec timestamps */}
              <div className="border-t border-gray-200 mt-6 pt-4 text-xs text-gray-500 space-y-1">
                <div className="flex justify-between">
                  <span>Créé le: {formatDateTime(selectedBatch?.created_at)}</span>
                  <span>Mis à jour: {formatDateTime(selectedBatch?.updated_at)}</span>
                </div>
                {selectedBatch?.case_id && (
                  <div>ID Cas: {selectedBatch.case_id}</div>
                )}
                {(selectedBatch?.responsible || selectedBatch?.responsibleId) && (
                  <div>Responsable ID: {selectedBatch.responsible || selectedBatch.responsibleId}</div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
            <Icon name="QrCode" size={64} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun lot sélectionné</h3>
            <p className="text-sm text-gray-600">
              Sélectionnez un lot dans la liste pour générer sa fiche de production complète
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default QRCodeGenerator;